/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * CUSTOM ENGRAVING FEATURE (ported from Focal 9.1.1 to the Focal 13.0.0 component architecture)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Focal 13 re-renders the variant picker server-side on every option change (fetch + swap), instead of
 * computing variant combinations client-side like 9.1.1 did. Rather than patching the compiled theme.js
 * bundle to hook into that internal rendering (which would silently break again on the next theme update,
 * exactly like this feature just did), this implementation relies only on the theme's public, documented
 * extension points:
 *
 * - "variant:changed" (bubbles) - fired on the product form whenever the selected variant changes.
 * - "variant:added" (bubbles) - fired on the product form after a successful add to cart.
 * - "openable-element:load:end" (does NOT bubble, but capture-phase listeners on document still see it,
 *   because the capture phase always runs top-down regardless of a given event's "bubbles" flag) - fired
 *   whenever a drawer/popover (e.g. a quick-buy drawer on a collection page) finishes lazy-loading its
 *   product markup via AJAX.
 * - a capturing "submit" listener on document, which runs before the theme's own <product-form> submit
 *   handler (custom elements' listeners fire at the "target" phase, which always happens after capture),
 *   letting us block an invalid add-to-cart without touching the core add-to-cart flow at all.
 *
 * This keeps the feature update-safe and avoids any dependency on theme.js internals, private class fields,
 * or DOM structure that Shopify/Maestrooo may change again in a future release.
 */
(function () {
  "use strict";

  // Matches emoji / pictographs so they can be stripped from the engraving message as the user types.
  var EMOJI_REGEX = /(?:[✀-➿]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[#-9]️?⃣|㊙|㊗|〽|〰|Ⓜ|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|🆎|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|🈚|🈯|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|‼|⁉|[▪-▫]|▶|◀|[◻-◾]|©|®|™|ℹ|🀄|[☀-⛿]|⬅|⬆|⬇|⬛|⬜|⭐|⭕|⌚|⌛|⌨|⏏|[⏩-⏳]|[⏸-⏺]|🃏|⤴|⤵|[←-⇿])/g;

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/([^\w-])/g, "\\$1");
  }

  function parseEngravingOptions(value) {
    if (!value) return [];
    return value
      .split(",")
      .map(function (item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  /**
   * NOTE: we deliberately do NOT resolve the variant-picker through the form's "id" attribute/property here.
   * Some third-party scripts on the storefront (cart/upsell apps, floating-label scripts...) have been observed
   * overwriting a form element's ".id" DOM property after page load (e.g. to a stray value like
   * "input.is-filled"), which breaks any lookup based on `form.id`. The product ID is not touched by those
   * scripts, so we join the form and its variant-picker through "data-prod-id"/"data-product-id" instead.
   */
  function getVariantPickerForProduct(productId) {
    if (!productId) return null;
    return document.querySelector('variant-picker[data-product-id="' + cssEscape(productId) + '"]');
  }

  function getEngravingOptionsForProduct(productId) {
    var picker = getVariantPickerForProduct(productId);
    if (!picker) return [];
    return parseEngravingOptions(picker.getAttribute("data-engraving-options"));
  }

  function variantMatchesEngraving(variantTitle, engravingOptions) {
    if (!variantTitle || !engravingOptions.length) return false;
    return engravingOptions.some(function (option) {
      return variantTitle.indexOf(option) !== -1;
    });
  }

  function getEngravingDrawer(productId) {
    return document.getElementById("product-" + productId + "-custom-engraving-drawer");
  }

  function getInitialVariant(form) {
    var productId = form.getAttribute("data-prod-id");
    var picker = getVariantPickerForProduct(productId);
    var script = picker && picker.querySelector("script[data-variant]");
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || "{}");
    } catch (error) {
      return null;
    }
  }

  /**
   * Shows/hides the engraving drawer sub-sections, toggles whether the hidden line item property inputs are
   * actually submitted with the form (by adding/removing their "name" attribute), and syncs the drawer's own
   * submit button label/state with the currently selected variant.
   *
   * Returns whether the variant requires engraving, so callers can detect a false -> true transition (used to
   * auto-open the drawer only when the user just switched into an engraving-enabled option).
   */
  function applyEngravingState(form, variant) {
    var productId = form.getAttribute("data-prod-id");
    if (!productId) return false;

    var engravingOptions = getEngravingOptionsForProduct(productId);
    var isEngraving = !!variant && variantMatchesEngraving(variant.title, engravingOptions);

    var propertyInputs = document.querySelectorAll(".engraving-property-" + productId);
    propertyInputs.forEach(function (input) {
      if (isEngraving) {
        input.setAttribute("name", input.getAttribute("data-name"));
      } else {
        input.removeAttribute("name");
      }
    });

    var drawer = getEngravingDrawer(productId);
    if (drawer) {
      var swatchers = drawer.querySelector(".custom-engraving-drawer-swatchers");
      var message = drawer.querySelector(".custom-engraving-drawer-message");
      var footerText = drawer.querySelector(".engraving_drawer_footer_text");
      var submitButton = drawer.querySelector(".engraving_drawer_submit_button");

      [swatchers, message, footerText].forEach(function (element) {
        if (element) element.style.display = isEngraving ? "" : "none";
      });

      if (submitButton) {
        if (variant && variant.available === false) {
          submitButton.textContent = submitButton.getAttribute("data-sold-text") || submitButton.textContent;
          submitButton.setAttribute("disabled", "disabled");
        } else {
          submitButton.textContent = isEngraving
            ? submitButton.getAttribute("data-engraving-text") || submitButton.textContent
            : submitButton.getAttribute("data-tocart-text") || submitButton.textContent;
          submitButton.removeAttribute("disabled");
        }
      }
    }

    form.setAttribute("data-engraving-active", isEngraving ? "1" : "0");
    return isEngraving;
  }

  /**
   * Mirrors the checked/value state, as well as the availability styling (is-disabled/hidden), of the real
   * variant-picker selectors onto their "engraving-drawer-*" counterparts inside the drawer, so both selector
   * sets stay visually consistent after a variant change.
   *
   * IMPORTANT: this must run AFTER the real variant-picker has been swapped in by the theme's own
   * "product:rerender" handling. "variant:changed" fires BEFORE that swap happens, so callers must defer this
   * (e.g. via setTimeout) rather than calling it synchronously from a "variant:changed" listener.
   */
  function syncDrawerFromForm(form) {
    var productId = form.getAttribute("data-prod-id");
    var drawer = productId && getEngravingDrawer(productId);
    var picker = getVariantPickerForProduct(productId);
    if (!drawer || !picker) return;

    // Mirror the checked/value state of every real option input onto its "engraving-drawer-*" counterpart.
    // We explicitly set the checked state to false for non-matching values too, because the drawer's mirrored
    // radios are NOT part of the real <form> (no "form" attribute), so the browser does not automatically
    // enforce mutual exclusivity between them the way it does for the real, form-associated radios.
    picker.querySelectorAll("[data-option-position]").forEach(function (realInput) {
      var position = realInput.getAttribute("data-option-position");
      var mirrorName = "engraving-drawer-option" + position;

      if (realInput.tagName === "SELECT") {
        var mirrorSelect = drawer.querySelector('select[name="' + mirrorName + '"]');
        if (mirrorSelect) mirrorSelect.value = realInput.value;
        return;
      }
      if (realInput.type === "hidden") return;

      var mirrorInput = drawer.querySelector(
        'input[name="' + mirrorName + '"][value="' + cssEscape(realInput.value) + '"]'
      );
      if (mirrorInput) mirrorInput.checked = realInput.checked;
    });

    var realSelectors = picker.querySelectorAll(".product-form__option-selector");
    var mirrorSelectors = drawer.querySelectorAll(".product-form__option-selector");
    realSelectors.forEach(function (realSelector, selectorIndex) {
      var mirrorSelector = mirrorSelectors[selectorIndex];
      if (!mirrorSelector) return;
      var realItems = realSelector.querySelectorAll(".color-swatch, .variant-swatch, .block-swatch");
      var mirrorItems = mirrorSelector.querySelectorAll(".color-swatch, .variant-swatch, .block-swatch");
      realItems.forEach(function (item, itemIndex) {
        var mirrorItem = mirrorItems[itemIndex];
        if (!mirrorItem) return;
        mirrorItem.classList.toggle("is-disabled", item.classList.contains("is-disabled"));
        mirrorItem.toggleAttribute("hidden", item.hasAttribute("hidden"));
      });
    });
  }

  /**
   * Runs the initial (page-load) engraving state for every product form found within the given scope. Used
   * both on DOMContentLoaded and whenever a quick-buy drawer/popover lazily loads new product markup.
   */
  function initEngravingScope(scope) {
    var forms = scope.querySelectorAll ? scope.querySelectorAll('form[is="product-form"][data-prod-id]') : [];
    forms.forEach(function (form) {
      applyEngravingState(form, getInitialVariant(form));
    });
  }

  // ------------------------------------------------------------------------------------------------------
  // Font swatch selection + message textarea (character counter, emoji stripping) inside the drawer.
  // ------------------------------------------------------------------------------------------------------
  document.addEventListener("change", function (event) {
    var target = event.target;
    if (!target.closest) return;

    var drawer = target.closest("drawer-content");
    if (!drawer || !drawer.classList.contains("custom-engraving-drawer")) return;

    // 1) Font swatch radios (Standard / Handschrift / Romantik)
    if (target.matches(".custom-engraving-drawer-swatchers .block-swatch__radio")) {
      var valueLabel = drawer.querySelector(".custom-engraving-drawer-swatchers .product-form__option-value");
      if (valueLabel) valueLabel.textContent = target.value;

      var messageField = drawer.querySelector(".custom-engraving-message-" + drawer.getAttribute("data-product-id"));
      if (messageField) messageField.setAttribute("data-font", target.value.toLowerCase());

      var fontInput = document.querySelector(".custom-engraving-font-property-" + drawer.getAttribute("data-product-id"));
      if (fontInput) fontInput.value = target.value;
      return;
    }

    // 2) Mirrored option selectors inside the drawer -> sync back to the real (visible) selectors
    if (target.matches('[name^="engraving-drawer-option"]')) {
      var value = target.value;
      var name = target.name.replace("engraving-drawer-", "");
      var realInput = document.querySelector(
        'input[name="' + name + '"][value="' + cssEscape(value) + '"], select[name="' + name + '"]'
      );
      if (!realInput) return;

      var shouldTriggerChange = false;
      if (realInput.tagName === "SELECT") {
        shouldTriggerChange = realInput.value !== value;
        realInput.value = value;
      } else {
        shouldTriggerChange = !realInput.checked;
        realInput.checked = true;
      }
      if (shouldTriggerChange) {
        realInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  });

  document.addEventListener("input", function (event) {
    var target = event.target;
    if (!target.matches || !target.matches(".custom-engraving-message")) return;

    var drawer = target.closest("drawer-content");
    var productId = drawer && drawer.getAttribute("data-product-id");
    var counter = drawer && drawer.querySelector(".characters-left-value");

    var enteredText = target.value;
    var strippedText = enteredText.replace(EMOJI_REGEX, "");
    if (enteredText.length !== strippedText.length) {
      target.value = strippedText;
      enteredText = strippedText;
    }

    var maxLength = parseInt(target.getAttribute("maxlength"), 10) || 0;
    if (counter) counter.textContent = String(maxLength - enteredText.length);

    if (productId) {
      var messageInput = document.querySelector(".custom-engraving-message-property-" + productId);
      if (messageInput) messageInput.value = enteredText;
    }

    target.classList.remove("error_outline");
  });

  // ------------------------------------------------------------------------------------------------------
  // React to the theme's own variant change (fired after every server re-render of the variant picker).
  // ------------------------------------------------------------------------------------------------------
  document.addEventListener("variant:changed", function (event) {
    var form = event.target;
    if (!form.getAttribute || !form.getAttribute("data-prod-id")) return;

    var variant = event.detail && event.detail.variant;
    var wasEngraving = form.getAttribute("data-engraving-active") === "1";
    var isEngraving = applyEngravingState(form, variant);

    // Defer: the real variant-picker DOM is only swapped in AFTER "variant:changed" fires (via the theme's
    // own, separate "product:rerender" handling), so reading its state synchronously here would still see
    // the PREVIOUS selection. A 0ms timeout runs after that swap has completed.
    window.setTimeout(function () {
      syncDrawerFromForm(form);
    }, 0);

    // Only auto-open the drawer when the user just switched INTO an engraving option, not on every
    // unrelated option change (e.g. picking a different color) while already in engraving mode.
    if (isEngraving && !wasEngraving) {
      var drawer = getEngravingDrawer(form.getAttribute("data-prod-id"));
      if (drawer) drawer.open = true;
    }
  });

  // ------------------------------------------------------------------------------------------------------
  // Reset the engraving message + property once the item has actually been added to the cart.
  // ------------------------------------------------------------------------------------------------------
  document.addEventListener("variant:added", function (event) {
    var form = event.target;
    var productId = form.getAttribute && form.getAttribute("data-prod-id");
    if (!productId) return;

    var propertyInput = document.querySelector(".custom-engraving-message-property-" + productId);
    if (propertyInput) propertyInput.value = "";

    var messageInput = document.querySelector(".custom-engraving-message-" + productId);
    var drawer = getEngravingDrawer(productId);
    if (messageInput) {
      messageInput.value = "";
      messageInput.classList.remove("error_outline");
      var counter = drawer && drawer.querySelector(".characters-left-value");
      if (counter) counter.textContent = messageInput.getAttribute("maxlength");
    }
    if (drawer) drawer.open = false;
  });

  // ------------------------------------------------------------------------------------------------------
  // Block add-to-cart when engraving is required but the message is empty. Runs in the capture phase so it
  // executes BEFORE the theme's own <product-form> submit handler (which starts the AJAX add-to-cart call).
  // ------------------------------------------------------------------------------------------------------
  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (!form.matches || !form.matches('form[is="product-form"]')) return;

      var productId = form.getAttribute("data-prod-id");
      if (!productId) return;

      var propertyInput = document.querySelector(".custom-engraving-message-property-" + productId);
      var messageInput = document.querySelector(".custom-engraving-message-" + productId);
      var drawer = getEngravingDrawer(productId);
      if (!propertyInput || !messageInput || !drawer) return;

      var isRequired = !!propertyInput.getAttribute("name");
      if (isRequired && messageInput.value.trim() === "") {
        event.preventDefault();
        event.stopImmediatePropagation();

        messageInput.classList.add("error_outline");
        drawer.open = true;

        window.requestAnimationFrame(function () {
          var content = messageInput.closest(".custom-engraving-drawer-content");
          if (content) content.scrollIntoView({ block: "center" });
          messageInput.focus();
        });
      }
    },
    true
  );

  // ------------------------------------------------------------------------------------------------------
  // Re-initialize the feature whenever a quick-buy drawer/popover lazily loads its product markup. This
  // event does not bubble, but a capturing listener on document still sees it, since the capture phase
  // always runs top-down before the "bubbles" flag comes into play.
  // ------------------------------------------------------------------------------------------------------
  document.addEventListener(
    "openable-element:load:end",
    function (event) {
      if (event.target && event.target.querySelector) initEngravingScope(event.target);
    },
    true
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initEngravingScope(document);
    });
  } else {
    initEngravingScope(document);
  }

  // ------------------------------------------------------------------------------------------------------
  // Delay the mobile sticky "In den Warenkorb"-Leiste (<product-sticky-form>, from product-sticky-form.liquid).
  // The theme reveals it as soon as the main Add-to-cart button has scrolled out of view (see the
  // "ProductStickyForm" custom element in theme.js). On mobile only, additionally require the visitor to
  // scroll STICKY_FORM_EXTRA_DELAY_PX further past that point before it's actually shown - desktop is left
  // completely untouched, and none of the theme's own logic (footer visibility etc.) is changed, we just
  // hold the reveal back a bit longer on small screens.
  // ------------------------------------------------------------------------------------------------------
  var STICKY_FORM_EXTRA_DELAY_PX = 1100;

  // Hardcoded rather than read from window.themeVariables.breakpoints.pocket - that object is only
  // guaranteed to exist once the theme's own (deferred) scripts have run, and custom.js could execute
  // before it's ready, silently disabling this whole feature. "740px" matches the theme's own pocket/
  // mobile cutoff used consistently throughout theme.css (e.g. all "max-width: 740px" rules).
  var MOBILE_QUERY = "(max-width: 740px)";

  function setupDelayedStickyForm(form) {
    if (form.__delayedStickySetup) return;
    form.__delayedStickySetup = true;

    var pocketQuery = (window.themeVariables && window.themeVariables.breakpoints && window.themeVariables.breakpoints.pocket) || MOBILE_QUERY;

    // IMPORTANT: the theme's <product-sticky-form> class only defines a SETTER for "hidden"
    // (set hidden(value) { this.toggleAttribute(...) ... }) - there is no getter. That means
    // reading the PROPERTY (form.hidden) always returns undefined, regardless of the real state -
    // confirmed live via Object.getOwnPropertyDescriptor, which showed only a "set", no "get".
    // Every read below therefore has to go through the ATTRIBUTE instead (form.hasAttribute
    // ("hidden")), which reflects the true state correctly. Writes still use "form.hidden = value"
    // so the theme's own setter (and its --cart-notification-offset side effect) keeps running.
    function isHidden() {
      return form.hasAttribute("hidden");
    }

    var themeWantsVisible = !isHidden();
    var triggerScrollY = null;
    var applying = false;

    function isMobile() {
      return window.matchMedia(pocketQuery).matches;
    }

    function applyVisibility() {
      if (!isMobile()) {
        // Desktop (or any other breakpoint) - don't interfere, leave the theme's own state as-is.
        triggerScrollY = null;
        return;
      }

      if (!themeWantsVisible) {
        triggerScrollY = null;
        return;
      }

      if (triggerScrollY === null) {
        triggerScrollY = window.scrollY;
      }

      var shouldShow = window.scrollY - triggerScrollY >= STICKY_FORM_EXTRA_DELAY_PX;

      if (shouldShow && isHidden()) {
        applying = true;
        form.hidden = false;
        queueMicrotask(function () {
          applying = false;
        });
      } else if (!shouldShow && !isHidden()) {
        applying = true;
        form.hidden = true;
        queueMicrotask(function () {
          applying = false;
        });
      }
    }

    // The theme's own "hidden" setter (toggles the attribute + a CSS offset variable) runs from ITS OWN
    // IntersectionObserver callback - we can't hook into that directly, so instead we watch the resulting
    // "hidden" ATTRIBUTE change. "applying" guards against reacting to the mutations WE cause ourselves
    // below (queueMicrotask defers clearing it until after this same mutation has been delivered to the
    // observer, since MutationObserver callbacks are themselves microtasks queued in call order).
    var observer = new MutationObserver(function () {
      if (applying) return;
      themeWantsVisible = !isHidden();
      applyVisibility();
    });
    observer.observe(form, { attributes: true, attributeFilter: ["hidden"] });

    window.addEventListener("scroll", applyVisibility, { passive: true });
    window.addEventListener("resize", applyVisibility);
  }

  function initDelayedStickyForm(scope) {
    scope.querySelectorAll("product-sticky-form").forEach(setupDelayedStickyForm);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initDelayedStickyForm(document);
    });
  } else {
    initDelayedStickyForm(document);
  }

  // The sticky form lives inside a <product-rerender> wrapper (see product-sticky-form.liquid)
  // that fetches a brand-new <product-sticky-form> element from the server and swaps it in every
  // time the visitor changes a variant (same "product:rerender" mechanism referenced in the
  // engraving code above). That replacement node is a fresh DOM element with no
  // "__delayedStickySetup" flag and no MutationObserver attached to it, so without this, the very
  // next variant change would silently drop the delay and fall back to the theme's native
  // (undelayed) sticky-bar behavior. Watch the page for any newly inserted
  // <product-sticky-form> element and (re-)apply the delayed-show setup to it.
  var stickyFormRerenderObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches("product-sticky-form")) {
          setupDelayedStickyForm(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll("product-sticky-form").forEach(setupDelayedStickyForm);
        }
      });
    });
  });

  if (document.body) {
    stickyFormRerenderObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener("DOMContentLoaded", function () {
      stickyFormRerenderObserver.observe(document.body, { childList: true, subtree: true });
    });
  }
})();